<template>
	<div
		class="provision-offer-card-detail template-layout premium-shop-item__content"
	>
		<div class="premium-shop-item__character-wrapper">
			<div
				class="premium-shop-item__character-image"
				:class="`character--${contentData.attributes?.skin}-130`"
			/>
		</div>
		<div
			class="premium-shop-item__values"
			:class="{
				'premium-shop-item__values--offset-right':
					hasPercentageDiscount,
			}"
		>
			<div class="rewards-container">
				<template
					v-for="(reward, _index) in royalProvisionRewards"
					:key="_index"
				>
					<Tooltip
						:heading="translate(reward.title)"
						customWidth="18rem"
						:placement="Placements.TOP"
						class="reward-item"
					>
						<template #element>
							<div class="rewards-grid__value">
								<div
									v-if="reward.icon"
									:class="[`${reward.icon}-48`]"
								/>
							</div>
							<div class="rewards-grid__label">
								<span>upgrade</span>
							</div>
						</template>
						<template #tooltip>
							<span
								class="reward-tooltip"
								v-html="
									replacePlaceholder(
										replacePlaceholder(
											translate(reward.description),
											'{percent}',
											`<b>${reward?.percentage ?? 0}%</b>`,
										),
										'{number}',
										`<b>${reward?.number ?? 0}</b>`,
									)
								"
							/>
						</template>
					</Tooltip>
				</template>
			</div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import Tooltip from "@/components/Tooltip.vue";
import { royalProvisionRewards } from "@/game/config/shop";
import { type ShopItem } from "@/types/shop";
import { Placements } from "@/types/ui";
import { replacePlaceholder } from "@/utils/replacePlaceholder";
import { translate } from "@/utils/translate";
import { computed } from "vue";

interface Props {
	contentData: ShopItem;
}

const { contentData } = defineProps<Props>();

const hasPercentageDiscount = computed(
	(): boolean =>
		!!contentData.attributes.percentage_discount &&
		contentData.attributes.percentage_discount > 0,
);
</script>

<style lang="scss" scoped>
.premium-shop-item__content {
	position: relative;
	flex: 1 1 auto;
	width: 100%;
	min-height: 0;
	@include flex-row-center;
	align-items: flex-end;
	height: 100%;

	.premium-shop-item {
		&__character {
			&-wrapper {
				position: relative;
				flex: 0 0 auto;
				width: 8.5rem;
				height: 9.5rem;
			}

			&-image {
				position: absolute;
				bottom: 0;
				left: 0;
			}
		}

		&__values {
			flex: 1 1 auto;
			@include flex-col-center;
			justify-content: flex-start;
			padding-bottom: 2.5rem;
			height: 100%;
			overflow: hidden;

			&--offset-right {
				padding-right: 1.625rem;
			}

			.rewards-grid {
				width: 100%;
			}

			.rewards-container {
				display: grid;
				grid-template-rows: 48px 8px 48px;
				grid-template-columns: 48px 48px 48px;
				justify-content: center;
				align-items: center;
				gap: 0.25rem;
				padding: 0.25rem;

				.rewards-grid {
					@include flex-row-center;
					position: relative;
					z-index: 1;
					padding: 1.25rem 0.625rem 1.125rem;

					&__item {
						@include flex-col-center;

						.v-popper {
							width: 100%;
							height: 100%;
						}
					}

					&__value {
						@include flex-row-center;
						position: relative;
						width: 100%;
						height: 100%;
					}

					&__label {
						@include flex-row-center;
						position: relative;
						left: -5%;
						mask-image: linear-gradient(
							to right,
							transparent 2%,
							black 20%,
							black 80%,
							transparent 98%
						);
						margin-top: -0.5rem;
						border-width: 0.063rem 0 0.063rem 0;
						border-style: solid;
						border-image-source: linear-gradient(
							to bottom,
							rgba(216, 169, 114, 1),
							rgba(226, 181, 145, 1)
						);
						border-image-slice: 1;
						width: 110%;

						&::before {
							position: absolute;
							top: 0;
							left: 0;
							@include background(
								url($path-textures + "silk-flag-texture-bg.avif"),
								cover,
								center center,
								no-repeat
							);
							z-index: 1;
							mix-blend-mode: multiply;
							width: 100%;
							height: 100%;
							content: "";
						}

						&::after {
							position: absolute;
							top: 0;
							left: 0;
							background: rgb(112, 62, 16);
							width: 100%;
							height: 100%;
							content: "";
						}

						span {
							position: relative;
							z-index: 1;
							padding-bottom: 0.0625rem;
							color: #fff3dd;
							font-weight: 800;
							font-size: 0.5rem;
							line-height: 1;
							text-shadow: 0.044rem 0.044rem 0.063rem
								rgba(0, 0, 0, 0.6);
							text-transform: uppercase;
						}
					}
				}
			}

			.reward-item:nth-child(1) {
				grid-row: 2; /* LEFT */
				grid-column: 1;
			}

			.reward-item:nth-child(2) {
				grid-row: 1; /* TOP */
				grid-column: 2;
			}

			.reward-item:nth-child(3) {
				grid-row: 2; /* RIGHT */
				grid-column: 3;
			}

			.reward-item:nth-child(4) {
				grid-row: 3; /* BOTTOM */
				grid-column: 2;
			}
		}
	}
}
</style>
